import emailjs from "emailjs-com";
import { getConfirmationMessage } from "./getConfirmationMessage";

const formatCurrency = (value) => {
  return new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
  }).format(value);
};

export const getEvents = async (includePrivate = false) => {
  try {
    const url = includePrivate
      ? `${process.env.REACT_APP_BACKEND_URL}/google-sheets/events?includePrivate=true`
      : `${process.env.REACT_APP_BACKEND_URL}/google-sheets/events`;
    const response = await fetch(url);
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("api.js: Error retrieving events:", error);
    return null;
  }
};

export const getEventByName = async (eventName) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/google-sheets/events/${encodeURIComponent(eventName)}`,
    );
    if (!response.ok) return null;
    return await response.json();
  } catch (error) {
    console.error("api.js: Error retrieving event:", error);
    return null;
  }
};

export const createEventPaymentIntent = async (eventName) => {
  const response = await fetch(
    `${process.env.REACT_APP_BACKEND_URL}/google-sheets/events/create-payment-intent`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ eventName }),
    },
  );
  if (!response.ok) return null;
  const data = await response.json();
  return data.clientSecret;
};

export const registerForEvent = async ({
  eventName,
  name,
  email,
  phone,
  dietaryRestrictions,
  amountPaid,
  paidWith,
}) => {
  const response = await fetch(
    `${process.env.REACT_APP_BACKEND_URL}/google-sheets/events/register`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        eventName,
        name,
        email,
        phone,
        dietaryRestrictions,
        amountPaid,
        paidWith,
      }),
    },
  );
  if (!response.ok) throw new Error("Registration failed");
  return response.json();
};

export const createDonationPaymentIntent = async (amountCents) => {
  const response = await fetch(
    `${process.env.REACT_APP_BACKEND_URL}/stripe/create-payment-intent`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ amount: amountCents }),
    },
  );
  if (!response.ok) return null;
  const data = await response.json();
  return data.clientSecret;
};

export const createGratuityPaymentLink = async (eventName, name, email) => {
  const response = await fetch(
    `${process.env.REACT_APP_BACKEND_URL}/google-sheets/events/create-gratuity-link`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ eventName, name, email }),
    },
  );
  if (!response.ok) return null;
  const data = await response.json();
  return data.paymentLink;
};

export const getEventRegistrations = async (eventName) => {
  const response = await fetch(
    `${process.env.REACT_APP_BACKEND_URL}/google-sheets/events/${encodeURIComponent(eventName)}/registrations`,
  );
  if (!response.ok) return [];
  return response.json();
};

export const sendGratuityFollowUpEmail = async ({
  name,
  email,
  eventTitle,
  paymentLink,
}) => {
  const emailParams = {
    to_name: name,
    to_email: email,
    event_title: eventTitle,
    payment_link: paymentLink,
  };
  try {
    await emailjs.send(
      process.env.REACT_APP_EMAILJS_SERVICE_ID,
      process.env.REACT_APP_EMAILJS_GRATUITY_TEMPLATE_ID ||
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
      emailParams,
      process.env.REACT_APP_EMAILJS_PUBLIC_KEY,
    );
    return true;
  } catch (error) {
    console.error("api.js: Failed to send gratuity follow-up email:", error);
    return false;
  }
};

export const sendEventConfirmationEmail = async ({
  name,
  email,
  eventTitle,
  customFormLink,
  receiptUrl,
}) => {
  const emailParams = {
    to_name: name,
    to_email: email,
    event_title: eventTitle,
    custom_form_link: customFormLink || "",
    receipt_url: receiptUrl || "",
  };
  try {
    await emailjs.send(
      process.env.REACT_APP_EMAILJS_SERVICE_ID,
      process.env.REACT_APP_EMAILJS_EVENT_TEMPLATE_ID ||
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
      emailParams,
      process.env.REACT_APP_EMAILJS_PUBLIC_KEY,
    );
    return true;
  } catch (error) {
    console.error("api.js: Failed to send event confirmation email:", error);
    return false;
  }
};

export const addEvent = async (eventData) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/google-sheets/add-event`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(eventData),
      },
    );
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("api.js: Error adding event:", error);
    return error;
  }
};

export const postFormData = async (formData) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/google-sheets/add-report`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      },
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();

    return data;
  } catch (error) {
    console.error("api.js: Error submitting report:", error);
  }
};

export const postPendingEtransfer = async (formData) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/google-sheets/add-pending-etransfer`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      },
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("api.js: Error posting etransfer:", error);
  }
};

export const updateInventory = async (itemsPurchased, reduceInventory) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/google-sheets/update-inventory`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          itemsPurchased: itemsPurchased,
          reduceInventory: reduceInventory,
        }),
      },
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
  } catch (error) {
    console.error("api.js: Error updating inventory:", error);
  }
};

export const getProperties = async () => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/google-sheets/properties`,
    );
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error("api.js: Error retrieving properties:", error);
    return null;
  }
};

export const getBookingBounds = async (propertyId) => {
  try {
    const query = propertyId
      ? `?propertyId=${encodeURIComponent(propertyId)}`
      : "";
    const response = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/google-sheets/booking-bounds${query}`,
    );
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error("api.js: Error retrieving booking bounds:", error);
    return { MAX_FUTURE_DAYS: 60, MAX_BOOKING_DURATION: 5 };
  }
};

export const getBookingQuote = async (propertyId, startDate, endDate) => {
  try {
    const params = new URLSearchParams({ startDate, endDate });
    const response = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/google-sheets/properties/${encodeURIComponent(propertyId)}/quote?${params}`,
    );
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error("api.js: Error retrieving booking quote:", error);
    return null;
  }
};

export const createBookingPaymentIntent = async (
  propertyId,
  startDate,
  endDate,
) => {
  const response = await fetch(
    `${process.env.REACT_APP_BACKEND_URL}/google-sheets/properties/create-payment-intent`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ propertyId, startDate, endDate }),
    },
  );
  if (!response.ok) {
    console.error("HTTP Error:", response.statusText);
    return null;
  }
  const data = await response.json();
  return data?.clientSecret || null;
};

export const buildBookingReportData = ({
  name,
  email,
  propertyDisplayName,
  rentalStart,
  rentalEnd,
  totalAmount,
  paymentMethod,
}) => {
  const start =
    rentalStart instanceof Date ? rentalStart : new Date(rentalStart);
  const end = rentalEnd instanceof Date ? rentalEnd : new Date(rentalEnd);
  const nights = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
  const dateFormatter = new Intl.DateTimeFormat("en-CA", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  const paidWith =
    paymentMethod === "credit"
      ? "Credit"
      : paymentMethod === "etransfer"
        ? "Debit"
        : "None";

  return {
    date: new Date().toLocaleDateString("en-CA"),
    name: name.trim(),
    email: email.trim(),
    phone: "",
    total: `$${Number(totalAmount).toFixed(2)}`,
    pickupLocation: "N/A",
    itemsPurchased: `${propertyDisplayName} — ${dateFormatter.format(start)} to ${dateFormatter.format(end)} (${nights} night${nights !== 1 ? "s" : ""})`,
    orderId: Math.floor(Math.random() * 9000000000) + 1000000000,
    paidWith,
  };
};

export const logBookingPayment = async ({
  name,
  email,
  propertyDisplayName,
  rentalStart,
  rentalEnd,
  totalAmount,
  paymentMethod,
}) => {
  if (!totalAmount || totalAmount <= 0 || paymentMethod === "none") {
    return;
  }

  const reportData = buildBookingReportData({
    name,
    email,
    propertyDisplayName,
    rentalStart,
    rentalEnd,
    totalAmount,
    paymentMethod,
  });

  try {
    if (paymentMethod === "etransfer") {
      await postPendingEtransfer(reportData);
    }
    await postFormData(reportData);
  } catch (error) {
    console.error("api.js: Error logging booking payment:", error);
  }
};

export const getOrderDetails = async (orderId) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/google-sheets/get-order-details`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          orderId: orderId,
        }),
      },
    );
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("api.js: Error retrieving order details:", error);
  }
};

export const getStripeReceiptUrl = async (paymentIntentId) => {
  const response = await fetch(
    `${process.env.REACT_APP_BACKEND_URL}/stripe/receipt-url/${paymentIntentId}`,
  );
  if (!response.ok) return null;
  const data = await response.json();
  return data.receiptUrl;
};

export const createPaymentIntent = async (totalInCents) => {
  const response = await fetch(
    `${process.env.REACT_APP_BACKEND_URL}/stripe/create-payment-intent`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ amount: totalInCents }),
    },
  );
  if (!response.ok) {
    console.error("HTTP Error:", response.statusText);
    return null;
  }

  const paymentIntentData = await response.json();
  if (!paymentIntentData) {
    return null;
  }
  return paymentIntentData.clientSecret;
};
export const sendOrderConfirmationEmail = async ({
  name,
  emailAddress,
  costs,
  itemsPurchased,
  pickupLocation,
  orderId,
}) => {
  const emailParams = {
    to_name: name,
    to_email: emailAddress,
    order_subtotal: costs.subtotal,
    order_tax: costs.tax,
    order_credit_card_fee: costs.credit || "0",
    order_total: costs.total,
    order_coupon_savings: costs.couponSavings || "0",
    items_purchased: itemsPurchased,
    pickup_location: pickupLocation,
    order_id: orderId,
  };
  try {
    const emailResponse = await emailjs.send(
      process.env.REACT_APP_EMAILJS_SERVICE_ID,
      process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
      emailParams,
      process.env.REACT_APP_EMAILJS_PUBLIC_KEY,
    );
    return true;
  } catch (error) {
    console.error("api.js: Failed to send email:", error);
    return false;
  }
};

export const sendBookingConfirmationEmail = async (
  rentalName,
  name,
  bookingTime,
  emailAddress,
  rentalStart,
  rentalEnd,
  pricingModel = "gift_economy",
  amountPaid = 0,
  paymentMethod = "none",
) => {
  const options = {
    year: "numeric",
    month: "long",
    day: "numeric",
  };

  const formatter = new Intl.DateTimeFormat("en-US", options);
  const formattedDate = formatter.format(bookingTime);

  // Format rental dates if they're Date objects or date strings
  const formatRentalDate = (dateInput) => {
    if (dateInput instanceof Date) {
      return formatter.format(dateInput);
    } else if (typeof dateInput === "string") {
      // If it's already a formatted string, return as is
      return dateInput;
    }
    return dateInput; // Return as is if it's something else
  };

  const emailParams = {
    name: name.split(" ")[0],
    to_email: emailAddress,
    time: formattedDate,
    rental_start: formatRentalDate(rentalStart),
    rental_end: formatRentalDate(rentalEnd),
    message: getConfirmationMessage(
      rentalName,
      pricingModel,
      amountPaid,
      paymentMethod,
    ),
  };

  try {
    const emailResponse = await emailjs.send(
      process.env.REACT_APP_EMAILJS_SERVICE_ID,
      process.env.REACT_APP_EMAILJS_BOOKING_TEMPLATE_ID,
      emailParams,
      process.env.REACT_APP_EMAILJS_PUBLIC_KEY,
    );
    return true;
  } catch (error) {
    console.error("api.js: Failed to send booking confirmation email:", error);
    console.error("Error details:", error.text || error.message || error);
    return false;
  }
};
